import { useCallback, useEffect, useState } from "react";
import { StockLots } from "@/components/StockLots";
import { WastageReport } from "@/components/WastageReport";
import { BufferPlan } from "@/components/BufferPlan";
import { BatchQc } from "@/components/BatchQc";
import { ChefHat, AlertTriangle, CheckCircle2, Copy, ShoppingBasket } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader, DateNav, Money, Empty } from "@/components/common";
import { api, errMsg } from "@/lib/api";
import { todayIST, fmtDate, fmtDateTime, inr } from "@/lib/format";

function BatchDialog({ open, onClose, plan, onDone }) {
  const [qty, setQty] = useState({});
  const [notes, setNotes] = useState("");
  useEffect(() => { if (open) setQty(Object.fromEntries(plan.products.map((p) => [p.product_id, p.qty]))); }, [open, plan]);
  const submit = async () => {
    try {
      await api.post("/production/batches", { date: plan.date, notes, items: Object.entries(qty).map(([product_id, q]) => ({ product_id, qty: Number(q) || 0 })) });
      toast.success("Production recorded. Raw material stock has been updated.");
      onDone();
      onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="batch-dialog">
        <DialogHeader><DialogTitle className="font-display">Record production · {fmtDate(plan.date)}</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Adjust quantities if you made extra (buffer) packs. Raw materials are deducted from stock using the recipes.</p>
        <div className="space-y-3">
          {plan.products.map((p) => (
            <div key={p.product_id} className="flex items-center justify-between gap-3">
              <span className="text-sm font-medium">{p.name} <span className="text-muted-foreground">({p.unit_label})</span></span>
              <Input type="number" min="0" className="w-24" value={qty[p.product_id] ?? ""} onChange={(e) => setQty({ ...qty, [p.product_id]: e.target.value })} data-testid={`batch-qty-${p.product_id}`} />
            </div>
          ))}
          <Input placeholder="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="batch-notes" />
        </div>
        <DialogFooter><Button className="bg-[#0F382C] hover:bg-[#134E3A]" onClick={submit} data-testid="batch-submit-btn">Mark production done</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Production() {
  const [date, setDate] = useState(todayIST(1));
  const [plan, setPlan] = useState(null);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => api.get("/production/plan", { params: { date } }).then((r) => setPlan(r.data)).catch((e) => toast.error(errMsg(e))), [date]);
  useEffect(() => { load(); }, [load]);
  if (!plan) return <div className="h-64 animate-pulse rounded-3xl bg-white" />;
  const shortfall = plan.materials.filter((m) => m.shortfall > 0);
  const copyList = () => {
    navigator.clipboard?.writeText(`Market list for ${fmtDate(plan.date)}:\n` + shortfall.map((m) => `- ${m.name}: ${m.shortfall} ${m.unit}`).join("\n"));
    toast.success("Shopping list copied");
  };
  const totalPacks = plan.products.reduce((s, p) => s + p.qty, 0);

  return (
    <div data-testid="production-page">
      <PageHeader eyebrow="Production planning" title="What to grind" subtitle="Built automatically from the orders customers confirmed on WhatsApp.">
        <DateNav value={date} onChange={setDate} />
        <Button disabled={!plan.products.length} onClick={() => setOpen(true)} className="rounded-full bg-[#0F382C] hover:bg-[#134E3A]" data-testid="record-production-btn">
          <CheckCircle2 className="mr-2 h-4 w-4" />Record production
        </Button>
      </PageHeader>

      <div className="relative overflow-hidden rounded-3xl bg-emerald-950 p-6 sm:p-8 text-emerald-50 shadow-xl mb-8 bf-rise" data-testid="bom-card">
        <div className="absolute inset-0 bf-grain opacity-50" />
        <div className="relative grid grid-cols-2 gap-3 sm:grid-cols-5">
          {[["Orders", plan.orders_count, "plan-orders"], ["Packs to make", totalPacks, "plan-packs"], ["Raw material cost", inr(plan.total_cost), "plan-cost"], ["Sales value", inr(plan.revenue), "plan-revenue"], ["Gross profit", inr(plan.gross_profit), "plan-profit"]].map(([l, v, id]) => (
            <div key={l} className="rounded-2xl bg-white/10 p-4 backdrop-blur-md">
              <div className="text-[11px] uppercase tracking-wider text-emerald-200/80">{l}</div>
              <div className="font-mono text-xl sm:text-2xl font-semibold mt-1" data-testid={id}>{v}</div>
            </div>
          ))}
        </div>
        {plan.pending_responses > 0 && (
          <div className="relative mt-4 inline-flex items-center gap-2 rounded-full bg-amber-400/20 px-3 py-1 text-sm text-amber-200" data-testid="pending-warning">
            <AlertTriangle className="h-4 w-4" />{plan.pending_responses} customers haven't replied yet
          </div>
        )}
      </div>

      {plan.products.length > 0 && (
        <div className={`mb-4 rounded-xl px-4 py-2 text-sm ${plan.buffer_applied ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-800"}`} data-testid="buffer-applied-banner">
          {plan.buffer_applied ? `Includes ${plan.buffer_packs} extra pack(s) approved by ${plan.approved_by}. Kitchen plan and raw materials below cover orders + buffer.` : "Orders only. No extra packs yet: approve the production plan further down to add the buffer."}
        </div>
      )}
      {plan.kitchen?.length > 0 && (
        <div className="mb-8 grid gap-4 md:grid-cols-2" data-testid="kitchen-summary">
          {plan.kitchen.map((k, i) => (
            <div key={k.batter} className="rounded-2xl border-2 border-[#0F382C] bg-white p-5" data-testid={`kitchen-batter-${i}`}>
              <div className="text-xs uppercase tracking-wider text-[#D96B43]">Grind today</div>
              <div className="mt-1 font-display text-2xl font-bold text-[#0F382C]" data-testid={`kitchen-total-${i}`}>{k.total_kg} kg {k.batter}</div>
              <div className="mt-3 space-y-1.5">
                {k.packs.map((p) => (
                  <div key={p.label} className="flex items-center justify-between rounded-xl bg-[#FBF9F4] px-4 py-2.5" data-testid={`kitchen-pack-${i}-${p.label.replace(" ", "")}`}>
                    <span className="text-sm">Pack in <b>{p.label}</b> packets</span><span className="font-mono text-xl font-semibold text-[#0F382C]">{p.packs}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {plan.products.length === 0 ? (
        <Empty icon={ChefHat} title="No confirmed orders for this date" text="Send WhatsApp confirmations first. Confirmed quantities appear here." testid="production-empty" />
      ) : (
        <div className="grid gap-6 lg:grid-cols-2">
          <div className="rounded-2xl border border-[#E7E2D6] bg-white p-5" data-testid="products-to-make">
            <div className="font-display text-lg font-semibold text-[#0F382C] mb-3">Products to make</div>
            <Table>
              <TableHeader><TableRow><TableHead>Product</TableHead><TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Cost/pack</TableHead><TableHead className="text-right">Total cost</TableHead></TableRow></TableHeader>
              <TableBody>
                {plan.products.map((p) => (
                  <TableRow key={p.product_id} data-testid={`plan-product-${p.product_id}`}>
                    <TableCell className="font-medium">{p.name}<div className="text-xs text-muted-foreground">{p.unit_label}</div></TableCell>
                    <TableCell className="text-right font-mono font-semibold">{p.qty}</TableCell>
                    <TableCell className="text-right"><Money value={p.unit_cost} /></TableCell>
                    <TableCell className="text-right"><Money value={p.cost} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="rounded-2xl border border-[#E7E2D6] bg-white p-5" data-testid="materials-needed">
            <div className="font-display text-lg font-semibold text-[#0F382C] mb-3">Raw materials needed</div>
            <Table>
              <TableHeader><TableRow><TableHead>Material</TableHead><TableHead className="text-right">Need</TableHead><TableHead className="text-right">In stock</TableHead><TableHead className="text-right">Cost</TableHead></TableRow></TableHeader>
              <TableBody>
                {plan.materials.map((m) => (
                  <TableRow key={m.material_id} className={m.shortfall > 0 ? "bg-rose-50/60" : ""}>
                    <TableCell className="font-medium">{m.name}</TableCell>
                    <TableCell className="text-right font-mono">{m.required} {m.unit}</TableCell>
                    <TableCell className={`text-right font-mono ${m.shortfall > 0 ? "text-rose-600 font-semibold" : ""}`}>{m.stock} {m.unit}</TableCell>
                    <TableCell className="text-right"><Money value={m.cost} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {shortfall.length > 0 && (
              <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-4" data-testid="shopping-list">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 font-semibold text-rose-700"><ShoppingBasket className="h-4 w-4" />Buy at market</div>
                  <Button size="sm" variant="outline" onClick={copyList} data-testid="copy-shopping-list"><Copy className="mr-1 h-3 w-3" />Copy</Button>
                </div>
                {shortfall.map((m) => <div key={m.material_id} className="text-sm mt-1">{m.name}: <span className="font-mono font-semibold">{m.shortfall} {m.unit}</span></div>)}
              </div>
            )}
          </div>
        </div>
      )}

      {plan.batches.length > 0 && (
        <div className="mt-8 rounded-2xl border border-emerald-200 bg-emerald-50/50 p-5" data-testid="batches-done">
          <div className="font-display font-semibold text-[#0F382C]">Production recorded for this date</div>
          {plan.batches.map((b) => (
            <div key={b.id} className="mt-2 flex flex-wrap items-center gap-2 text-sm" data-testid={`batch-row-${b.id}`}><span>{fmtDateTime(b.created_at)} by {b.by}: {b.items.map((i) => `${i.name} × ${i.qty}`).join(", ")} · cost <Money value={b.total_cost} className="font-semibold" /></span><BatchQc batch={b} onDone={load} /></div>
          ))}
        </div>
      )}
      {plan.products.length > 0 && <BufferPlan date={plan.date} onApproved={load} />}
      <StockLots />
      <WastageReport />
      <BatchDialog open={open} onClose={() => setOpen(false)} plan={plan} onDone={load} />
    </div>
  );
}
